import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, TrendingUp, DollarSign, Users, Shield, AlertTriangle,
  CheckCircle, PieChart, BarChart3, Target, Award, Briefcase
} from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { useProjectStore } from '../store/projectStore';
import { supabase } from '../lib/supabase';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from 'recharts';

interface FinancingAnalysis {
  overall_score: number;
  bankability_grade: 'A' | 'B' | 'C' | 'D';
  recommendation: 'APPROVED' | 'CONDITIONAL' | 'REVIEW' | 'REJECTED';
  economic_score: number;
  financial_security_score: number;
  social_impact_score: number;
  governance_score: number;
  strengths: Array<{ category: string; message: string }>;
  concerns: Array<{ category: string; severity: string; message: string }>;
  financial_data?: any;
}

export const FinancingAnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentProject, fetchProject, isLoading: projectLoading } = useProjectStore();
  const [analysis, setAnalysis] = useState<FinancingAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchProject(id);
      loadAnalysis();
    }
  }, [id]);

  const loadAnalysis = async () => {
    try {
      setIsLoading(true);

      // Try to load existing analysis
      const { data: existing, error: fetchError } = await supabase
        .from('financing_analyses')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        setAnalysis(existing as any);
      } else {
        // Calculate new analysis with sample data
        await calculateAnalysis();
      }
    } catch (error) {
      console.error('Error loading analysis:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalysis = async () => {
    if (!currentProject) return;

    try {
      // Mock financial data - in production, this would come from business plan
      const financialData = {
        revenue_growth: 15,
        margin: 18,
        has_multiple_revenue_streams: true,
        personal_contribution_ratio: 25,
        dscr: 1.4,
        min_cash_months: 3,
        jobs_created: 3,
        has_ess_label: true,
        local_suppliers_ratio: 60,
        has_experience: true,
        team_size: 2,
        year1_revenue: currentProject.funding_requested ? currentProject.funding_requested * 0.8 : 50000,
        year2_revenue: currentProject.funding_requested ? currentProject.funding_requested * 1.1 : 75000,
        year3_revenue: currentProject.funding_requested ? currentProject.funding_requested * 1.4 : 100000,
        personal_contribution: currentProject.funding_requested ? currentProject.funding_requested * 0.25 : 12500,
        grant: currentProject.funding_requested ? currentProject.funding_requested * 0.30 : 15000,
        loan_requested: currentProject.funding_requested ? currentProject.funding_requested * 0.45 : 22500
      };

      // Call calculation function
      const { data, error } = await supabase
        .rpc('calculate_financing_analysis', {
          p_project_id: id,
          p_financial_data: financialData
        });

      if (error) throw error;

      // Save analysis
      const { data: saved, error: saveError } = await supabase
        .from('financing_analyses')
        .insert({
          project_id: id,
          analyst_id: (await supabase.auth.getUser()).data.user?.id,
          overall_score: data.overall_score,
          bankability_grade: data.bankability_grade,
          recommendation: data.recommendation,
          economic_score: data.economic_score,
          financial_security_score: data.financial_security_score,
          social_impact_score: data.social_impact_score,
          governance_score: data.governance_score,
          strengths: data.strengths,
          concerns: data.concerns,
          financial_data: financialData
        })
        .select()
        .single();

      if (saveError) throw saveError;

      setAnalysis({ ...saved, financial_data: financialData } as any);
    } catch (error) {
      console.error('Error calculating analysis:', error);
    }
  };

  if (isLoading || projectLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-96">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!currentProject || !analysis) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-orange-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Analyse non disponible</h2>
            <button
              onClick={() => navigate('/projects')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retour aux projets
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const getGradeColor = (grade: string) => {
    const colors = {
      A: 'bg-green-500',
      B: 'bg-blue-500',
      C: 'bg-orange-500',
      D: 'bg-red-500'
    };
    return colors[grade as keyof typeof colors] || 'bg-gray-500';
  };

  const getGradeTextColor = (grade: string) => {
    const colors = {
      A: 'text-green-600',
      B: 'text-blue-600',
      C: 'text-orange-600',
      D: 'text-red-600'
    };
    return colors[grade as keyof typeof colors] || 'text-gray-600';
  };

  const getRecommendationLabel = (rec: string) => {
    const labels = {
      APPROVED: 'Projet finançable',
      CONDITIONAL: 'Finançable avec conditions',
      REVIEW: 'À consolider',
      REJECTED: 'À revoir'
    };
    return labels[rec as keyof typeof labels] || rec;
  };

  const getRecommendationColor = (rec: string) => {
    const colors = {
      APPROVED: 'bg-green-100 text-green-800 border-green-200',
      CONDITIONAL: 'bg-blue-100 text-blue-800 border-blue-200',
      REVIEW: 'bg-orange-100 text-orange-800 border-orange-200',
      REJECTED: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[rec as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const revenueData = analysis.financial_data ? [
    { year: 'Année 1', revenue: analysis.financial_data.year1_revenue || 0 },
    { year: 'Année 2', revenue: analysis.financial_data.year2_revenue || 0 },
    { year: 'Année 3', revenue: analysis.financial_data.year3_revenue || 0 }
  ] : [];

  const financingData = analysis.financial_data ? [
    { name: 'Apport personnel', value: analysis.financial_data.personal_contribution || 0, color: '#3b82f6' },
    { name: 'Subvention', value: analysis.financial_data.grant || 0, color: '#10b981' },
    { name: 'Prêt demandé', value: analysis.financial_data.loan_requested || 0, color: '#f59e0b' }
  ] : [];

  const scoresData = [
    { name: 'Viabilité\néconomique', score: analysis.economic_score, weight: '30%', color: '#3b82f6' },
    { name: 'Sécurité\nfinancière', score: analysis.financial_security_score, weight: '25%', color: '#10b981' },
    { name: 'Impact\nsocial', score: analysis.social_impact_score, weight: '25%', color: '#f59e0b' },
    { name: 'Gouvernance', score: analysis.governance_score, weight: '20%', color: '#8b5cf6' }
  ];

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour aux projets</span>
        </button>

        {/* HEADER - Synthèse */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Score Global */}
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-40 h-40">
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="#e5e7eb"
                    strokeWidth="10"
                    fill="none"
                  />
                  <circle
                    cx="80"
                    cy="80"
                    r="70"
                    stroke="currentColor"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray={`${(analysis.overall_score / 100) * 2 * Math.PI * 70} ${2 * Math.PI * 70}`}
                    strokeLinecap="round"
                    transform="rotate(-90 80 80)"
                    className={getGradeTextColor(analysis.bankability_grade)}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className={`text-6xl font-bold ${getGradeTextColor(analysis.bankability_grade)}`}>
                    {analysis.bankability_grade}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">{analysis.overall_score}/100</div>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mt-4">Score de Bancabilité</h3>
            </div>

            {/* Project Info */}
            <div className="lg:col-span-2 space-y-4">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentProject.name}</h2>
                <p className="text-gray-600">{currentProject.owner?.full_name || 'Porteur de projet'}</p>
              </div>

              <div className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg border ${getRecommendationColor(analysis.recommendation)}`}>
                <Award className="w-5 h-5" />
                <span className="font-semibold">{getRecommendationLabel(analysis.recommendation)}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-2 text-blue-600 mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm font-medium">Montant demandé</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">
                    {(currentProject.funding_requested || 0).toLocaleString('fr-FR')} €
                  </p>
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center space-x-2 text-green-600 mb-1">
                    <Target className="w-4 h-4" />
                    <span className="text-sm font-medium">Impact principal</span>
                  </div>
                  <p className="text-lg font-bold text-green-900">
                    {currentProject.domain || 'Économie Sociale et Solidaire'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* VIABILITÉ ÉCONOMIQUE */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Viabilité Économique</h3>
              <span className="ml-auto text-2xl font-bold text-blue-600">{analysis.economic_score}/100</span>
            </div>

            {revenueData.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Chiffre d'affaires prévisionnel (3 ans)</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip formatter={(value) => `${Number(value).toLocaleString('fr-FR')} €`} />
                    <Bar dataKey="revenue" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {analysis.financial_data && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Croissance CA</span>
                  <span className="font-semibold text-gray-900">{analysis.financial_data.revenue_growth}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Marge brute</span>
                  <span className="font-semibold text-gray-900">{analysis.financial_data.margin}%</span>
                </div>
              </div>
            )}
          </div>

          {/* STRUCTURE DE FINANCEMENT */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <PieChart className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Structure de Financement</h3>
            </div>

            {financingData.length > 0 && (
              <ResponsiveContainer width="100%" height={200}>
                <RechartsPie>
                  <Pie
                    data={financingData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${((entry.value / financingData.reduce((a, b) => a + b.value, 0)) * 100).toFixed(0)}%`}
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {financingData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${Number(value).toLocaleString('fr-FR')} €`} />
                </RechartsPie>
              </ResponsiveContainer>
            )}

            {analysis.financial_data && (
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Apport personnel</span>
                  <span className="font-semibold text-gray-900">{analysis.financial_data.personal_contribution_ratio}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Effet de levier</span>
                  <span className="font-semibold text-gray-900">
                    x{(financingData.reduce((a, b) => a + b.value, 0) / (analysis.financial_data.personal_contribution || 1)).toFixed(1)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* CAPACITÉ DE REMBOURSEMENT */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Shield className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Sécurité Financière</h3>
              <span className="ml-auto text-2xl font-bold text-green-600">{analysis.financial_security_score}/100</span>
            </div>

            {analysis.financial_data && (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-700">DSCR (Capacité remboursement)</span>
                    <span className="text-2xl font-bold text-green-900">{analysis.financial_data.dscr}</span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${Math.min((analysis.financial_data.dscr / 2) * 100, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {analysis.financial_data.dscr >= 1.5 ? 'Excellent' : analysis.financial_data.dscr >= 1.2 ? 'Bon' : 'Minimum requis'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Trésorerie min.</p>
                    <p className="text-lg font-bold text-gray-900">{analysis.financial_data.min_cash_months} mois</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Ratio apport</p>
                    <p className="text-lg font-bold text-gray-900">{analysis.financial_data.personal_contribution_ratio}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* IMPACT SOCIAL & GOUVERNANCE */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="space-y-4">
              {/* Social Impact */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <Users className="w-5 h-5 text-orange-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Impact Social</h3>
                  <span className="ml-auto text-2xl font-bold text-orange-600">{analysis.social_impact_score}/100</span>
                </div>
                {analysis.financial_data && (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                      <span className="text-gray-700">Emplois créés</span>
                      <span className="font-bold text-orange-900">{analysis.financial_data.jobs_created}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                      <span className="text-gray-700">Fournisseurs locaux</span>
                      <span className="font-bold text-orange-900">{analysis.financial_data.local_suppliers_ratio}%</span>
                    </div>
                    {analysis.financial_data.has_ess_label && (
                      <div className="flex items-center space-x-2 p-2 bg-orange-50 rounded">
                        <Award className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium text-orange-900">Labellisé ESS</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4">
                {/* Governance */}
                <div className="flex items-center space-x-2 mb-3">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Gouvernance</h3>
                  <span className="ml-auto text-2xl font-bold text-purple-600">{analysis.governance_score}/100</span>
                </div>
                {analysis.financial_data && (
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                      <span className="text-gray-700">Équipe</span>
                      <span className="font-bold text-purple-900">{analysis.financial_data.team_size} personne(s)</span>
                    </div>
                    {analysis.financial_data.has_experience && (
                      <div className="flex items-center space-x-2 p-2 bg-purple-50 rounded">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-900">Porteur expérimenté</span>
                      </div>
                    )}
                    {currentProject.assigned_advisor_id && (
                      <div className="flex items-center space-x-2 p-2 bg-purple-50 rounded">
                        <CheckCircle className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-900">Accompagnement CRESS</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SCORES DÉTAILLÉS */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Scores Détaillés (Pondérés)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={scoresData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Bar dataKey="score" fill="#3b82f6">
                {scoresData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-around mt-4">
            {scoresData.map((score, i) => (
              <div key={i} className="text-center">
                <div className="text-xs text-gray-500">{score.name.replace('\n', ' ')}</div>
                <div className="text-sm font-semibold" style={{ color: score.color }}>
                  Poids: {score.weight}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* POINTS FORTS & VIGILANCE */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Points forts */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Points Forts</h3>
            </div>
            <div className="space-y-2">
              {analysis.strengths.map((strength, index) => (
                <div key={index} className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-green-900">{strength.message}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Points de vigilance */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <h3 className="text-lg font-semibold text-gray-900">Points de Vigilance</h3>
            </div>
            <div className="space-y-2">
              {analysis.concerns.map((concern, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-2 p-3 rounded-lg border ${
                    concern.severity === 'critical' ? 'bg-red-50 border-red-200' :
                    concern.severity === 'high' ? 'bg-orange-50 border-orange-200' :
                    concern.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                >
                  <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    concern.severity === 'critical' ? 'text-red-600' :
                    concern.severity === 'high' ? 'text-orange-600' :
                    concern.severity === 'medium' ? 'text-yellow-600' :
                    'text-blue-600'
                  }`} />
                  <p className={`text-sm ${
                    concern.severity === 'critical' ? 'text-red-900' :
                    concern.severity === 'high' ? 'text-orange-900' :
                    concern.severity === 'medium' ? 'text-yellow-900' :
                    'text-blue-900'
                  }`}>{concern.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
